/** Castle siege offline / busy auto-resolve — show result modal from HTTP without racing `mvm:battle-ended`. */

export const MVM_CASTLE_AUTO_RESOLVE_EVENT = 'eclipsera:mvm-castle-auto-resolve'



export type MvmCastleAutoResolveDetail = {

  won: boolean

  you: 'attacker' | 'defender'

  message: string

  characterXp: number

  userXp: number

  attackId: string

  battleId?: string

  showResourceLoot?: boolean

  seizedResources?: Record<string, number> | null

  /** `castle` | `tower_pvp` | `barbarian_village` — gates the attacker Conquer/Raid choice. */

  attackType?: string

  /** Conquer/Raid alliance-task battle: the outcome is automatic, so no choice is offered. */

  allianceTaskBattle?: boolean

}



function normWallet(w: string | undefined | null) {

  return (w || '').toString().trim().toLowerCase()

}



const OFFLINE_AUTO_REASONS = new Set(['offline_power_resolve', 'attacker_busy_auto_resolve'])



function isOfflineAutoBattleDoc(battle: unknown): boolean {

  if (!battle || typeof battle !== 'object') return false

  const b = battle as { status?: string; winner?: { reason?: string } }

  return b.status === 'finished' && OFFLINE_AUTO_REASONS.has(String(b.winner?.reason || ''))

}



function resolveViewerSide(

  result: Record<string, unknown>,

  viewerWallet?: string,

): 'attacker' | 'defender' | null {

  const youRaw = String(result.you || '')

  if (youRaw === 'defender' || youRaw === 'attacker') return youRaw



  const wl = normWallet(viewerWallet)

  if (!wl) return null



  const battle = result.battle as

    | {

        attackerParticipants?: Array<{ walletAddress?: string }>

        defenderParticipants?: Array<{ walletAddress?: string }>

      }

    | undefined



  if (battle?.attackerParticipants?.some((p) => normWallet(p.walletAddress) === wl)) return 'attacker'

  if (battle?.defenderParticipants?.some((p) => normWallet(p.walletAddress) === wl)) return 'defender'



  const attW = normWallet(

    String(result.attackerWallet || battle?.attackerParticipants?.find((p) => p)?.walletAddress || ''),

  )

  if (attW === wl) return 'attacker'

  const defW = normWallet(String(result.defenderWallet || ''))

  if (defW === wl) return 'defender'

  return null

}



/** Parse auto-resolve fields from `attack-arrived` / `confirm-battle` HTTP or socket ack bodies. */

export function parseCastleAutoResolveFromApi(

  result: Record<string, unknown> | null | undefined,

  viewerWallet?: string,

): MvmCastleAutoResolveDetail | null {

  if (!result) return null



  const battle = result.battle

  const autoResolvedWithoutHex =

    result.autoResolvedWithoutHex === true || isOfflineAutoBattleDoc(battle)

  if (!autoResolvedWithoutHex) return null



  const attackIdRaw = result.attackId ?? result.pendingAttackId

  const attackId = attackIdRaw != null ? String(attackIdRaw).trim() : ''

  if (!attackId) return null



  const you = resolveViewerSide(result, viewerWallet) ?? 'attacker'

  const battleWinnerSide =

    (battle && typeof battle === 'object'

      ? (battle as { winner?: { side?: string } }).winner?.side

      : null) ||

    (typeof result.winningSide === 'string' ? result.winningSide : null)



  let won: boolean | null = typeof result.won === 'boolean' ? result.won : null

  if (won == null && battleWinnerSide) {

    won = battleWinnerSide === you

  }

  if (won == null) return null



  const charXp = won

    ? Number(result.characterXpWinner ?? 0) || 0

    : Number(result.characterXpLoser ?? 0) || 0

  const usrXp = won ? Number(result.userXpWinner ?? 0) || 0 : Number(result.userXpLoser ?? 0) || 0

  const showLoot =

    you === 'attacker' &&

    won &&

    result.resourcesSeized === true &&

    result.seizedResources &&

    typeof result.seizedResources === 'object'

  return {

    won,

    you,

    message: String(result.message || '').trim(),

    characterXp: charXp,

    userXp: usrXp,

    attackId,

    battleId: result.battleId != null ? String(result.battleId).trim() : undefined,

    showResourceLoot: !!showLoot,

    seizedResources:

      showLoot && result.seizedResources && typeof result.seizedResources === 'object'

        ? (result.seizedResources as Record<string, number>)

        : null,

    /** This handshake only ever carries castle sieges, but keep the server value when it sends one. */

    attackType:

      typeof result.attackType === 'string' && result.attackType.trim()

        ? result.attackType.trim()

        : 'castle',

    allianceTaskBattle: result.allianceTaskBattle === true,

  }

}



export function dispatchMvmCastleAutoResolve(detail: MvmCastleAutoResolveDetail) {

  if (typeof window === 'undefined') return

  window.dispatchEvent(new CustomEvent(MVM_CASTLE_AUTO_RESOLVE_EVENT, { detail }))

}



/** Dedupe key so HTTP + socket cannot open the same result modal twice. */

export function mvmCastleAutoResolveDedupeKey(detail: Pick<MvmCastleAutoResolveDetail, 'attackId' | 'battleId'>) {

  const aid = detail.attackId?.trim() || ''

  const bid = detail.battleId?.trim() || ''

  return `castle-auto:${aid}:${bid}`

}



export function isCastleAutoResolveForWallet(

  result: Record<string, unknown>,

  wallet: string,

): boolean {

  return resolveViewerSide(result, wallet) != null

}


